import { useState, useEffect, useCallback } from "react";
import {
  InvoiceEntity,
  InvoiceDataEntity,
} from "../../domain/entities/InvoiceEntity";
import { GetInvoiceByIdUseCase } from "../../domain/use-cases/invoices/GetInvoiceByIdUseCase";
import { InvoiceRepositoryImpl } from "../../data/repositories/InvoiceRepositoryImpl";

// Create repository instance (singleton)
const invoiceRepository = new InvoiceRepositoryImpl();

// Create use case
const getInvoiceByIdUseCase = new GetInvoiceByIdUseCase(invoiceRepository);

interface UseInvoiceDetailsReturn {
  invoiceDocument: InvoiceEntity | null;
  invoiceJson: InvoiceDataEntity | null;
  signedUrl: string | null;
  fileName: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseInvoiceDetailsOptions {
  initialInvoiceJson?: InvoiceDataEntity | null;
  initialInvoiceDocument?: InvoiceEntity | null;
  initialSignedUrl?: string | null;
  initialFileName?: string | null;
}

/**
 * Custom hook to fetch invoice details
 * - If initialInvoiceJson is provided (from location.state), uses it without fetching from API
 * - If no initial data, fetches from API
 * - React Router preserves location.state on navigate(-1), preventing unnecessary refetches
 */
export const useInvoiceDetails = (
  documentId: string | null,
  options?: UseInvoiceDetailsOptions
): UseInvoiceDetailsReturn => {
  const [invoiceDocument, setInvoiceDocument] = useState<InvoiceEntity | null>(
    options?.initialInvoiceDocument || null
  );
  const [invoiceJson, setInvoiceJson] = useState<InvoiceDataEntity | null>(
    options?.initialInvoiceJson || null
  );
  const [signedUrl, setSignedUrl] = useState<string | null>(
    options?.initialSignedUrl || null
  );
  const [fileName, setFileName] = useState<string | null>(
    options?.initialFileName || null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInvoiceDetails = useCallback(async () => {
    if (!documentId) {
      console.error("No documentId to fetch invoice details");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log("Fetching invoice details for documentId:", documentId);

      // Use the use case instead of calling API directly (Clean Architecture)
      const invoiceData = await getInvoiceByIdUseCase.execute(
        parseInt(documentId)
      );

      // Update invoice document state
      setInvoiceDocument(invoiceData);

      // Update signed URL and filename
      if (invoiceData.signed_url) {
        setSignedUrl(invoiceData.signed_url);
      }
      if (invoiceData.original_filename) {
        setFileName(invoiceData.original_filename);
      }

      // Update invoice JSON if available
      if (invoiceData.invoice_json) {
        setInvoiceJson(invoiceData.invoice_json);
      }

      console.log("Invoice details fetched successfully:", invoiceData);
    } catch (err) {
      console.error("Error fetching invoice details:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch invoice details";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  // Fetch on mount only if no initial data is provided
  useEffect(() => {
    // if (!documentId) {
    //   console.error("No documentId to fetch invoice details");
    //   return;
    // }

    // if (options?.initialInvoiceJson) {
    //   setInvoiceJson(options.initialInvoiceJson);
    //   return;
    // }

    // Only fetch if no initial data was provided
    console.log("🔄 No initial data, fetching from API...");
    fetchInvoiceDetails();
  }, [fetchInvoiceDetails, options?.initialInvoiceJson]);

  return {
    invoiceDocument,
    invoiceJson,
    signedUrl,
    fileName,
    loading,
    error,
    refetch: fetchInvoiceDetails,
  };
};
